/**
 * Compile an in-memory scope-filter tree (as edited by the FilterBuilder)
 * into the Orion Reporting/Scope wire format that buildOrionRequest emits
 * as `grouping.filter`.
 *
 * Pure / referentially-transparent. Returns either:
 *   - `null`            : tree is null/empty/all-stripped (caller omits the
 *                         field entirely on the grouping calc)
 *   - `string`          : single-preset shorthand ("HasValue" / "HasUnits")
 *                         when the entire tree reduces to one preset
 *   - `object`          : a `filter-collection` or a single-predicate object
 *                         in Orion's wire shape, ready for serialization
 *
 * Behavior rules:
 *   - Empty filter-collections are stripped (drop sub-collections that
 *     compile to null; if the whole tree is empty, return null).
 *   - A single child of a filter-collection collapses upward (no need to
 *     wrap one predicate in an All-of collection).
 *   - Preset sentinels:
 *       * Top-level + only filter on the grouping -> bare string form
 *         ("HasValue" / "HasUnits"). Orion's ContextFilterConverter expands
 *         these server-side; we use the short form when possible because
 *         it's the canonical idiom.
 *       * Composed with other filters -> expanded into the equivalent
 *         predicate (currency-not-equal on Activity.Value or
 *         decimal-not-equal on Activity.EndUnits, with haystack: [null, 0])
 *         so the surrounding filter-collection composes correctly.
 *   - Per-predicate `context` and `change` survive the walk untouched.
 *   - Predicate value normalization: drop empty haystack / needles arrays
 *     (the surrounding compileScopeFilter pass treats those as "no filter
 *     specified" rather than "match nothing", consistent with Orion's own
 *     null/Ignore short-circuiting). The whole predicate is dropped when
 *     it has no usable value -- mirroring Orion's semantic that an
 *     unconfigured filter doesn't accidentally zero the report.
 */

const PRESET_EXPANSIONS = {
  HasValue: { $type: 'currency-not-equal', path: 'Activity.Value', haystack: [null, 0] },
  HasUnits: { $type: 'decimal-not-equal', path: 'Activity.EndUnits', haystack: [null, 0] }
}

const VALID_METHODS = new Set(['All', 'Any'])

function expandPreset (preset) {
  return PRESET_EXPANSIONS[preset] ? { ...PRESET_EXPANSIONS[preset] } : null
}

function isPresetSentinel (node) {
  return node && node.$type === 'preset' && typeof node.preset === 'string'
}

function isCollection (node) {
  return node && node.$type === 'filter-collection'
}

/**
 * Returns true if the predicate has at least one usable value. Empty
 * arrays / undefined fields cause the predicate to be dropped from the
 * compiled output (so an unconfigured filter row doesn't accidentally
 * zero the report).
 */
function predicateHasValue (predicate) {
  if (!predicate || typeof predicate.$type !== 'string') return false
  const t = predicate.$type
  // Equal/not-equal/contains/starts-with/ends-with use haystack or needles.
  if (Array.isArray(predicate.haystack)) return predicate.haystack.length > 0
  if (Array.isArray(predicate.needles)) return predicate.needles.length > 0
  // Bool predicates use `value`.
  if (t.startsWith('bool-')) return predicate.value !== undefined
  // Range predicates need both bounds; lt/gt-style need their respective bound.
  if (t.endsWith('-range')) return predicate.lower !== undefined && predicate.upper !== undefined
  if (/-(less-than|less-than-equal)$/.test(t)) return predicate.upper !== undefined
  if (/-(greater-than|greater-than-equal)$/.test(t)) return predicate.lower !== undefined
  // Single-value compares (numeric/decimal/currency/percent/date/datetime equal/not-equal)
  if (/-(equal|not-equal)$/.test(t)) return predicate.value !== undefined || (Array.isArray(predicate.haystack) && predicate.haystack.length > 0)
  // Unknown shape: be permissive -- if it has any non-meta field, keep it.
  return Object.keys(predicate).some(k => k !== '$type' && k !== 'path' && k !== 'context' && k !== 'change')
}

/**
 * Normalizes a predicate for the wire format: drops `context` when it's
 * the default ('Self') or absent, and drops `change` when absent.
 * `$type`, `path`, and the operator's value field (`haystack` / `needles`
 * / `value` / `lower` / `upper`) are preserved verbatim -- they're the
 * substantive parts of the predicate Orion needs.
 */
function projectPredicate (predicate) {
  const out = { ...predicate }
  if (out.context === 'Self' || out.context == null) delete out.context
  if (out.change == null) delete out.change
  return out
}

/**
 * Recursively compiles a node. Returns:
 *   - the compiled node
 *   - or null when the node should be dropped
 *   - or a special PRESET marker when the node is a preset sentinel; the
 *     caller decides whether to use the bare-string form or the expansion
 */
function compileNode (node) {
  if (node == null) return null

  if (isPresetSentinel(node)) {
    // Don't expand here; let the parent decide. Compiler returns a marker
    // tuple via a wrapper object so we can later distinguish "preset alone"
    // from "preset composed with siblings".
    return { __preset: node.preset }
  }

  if (isCollection(node)) {
    const method = VALID_METHODS.has(node.method) ? node.method : 'All'
    const filters = Array.isArray(node.filters) ? node.filters : []
    const compiledChildren = []
    let presetChild = null

    for (const child of filters) {
      const compiled = compileNode(child)
      if (compiled == null) continue
      if (compiled.__preset) {
        // Hold onto preset markers separately so we can decide between
        // bare-string and expanded form once we know the sibling count.
        presetChild = compiled
      } else {
        compiledChildren.push(compiled)
      }
    }

    // If the only thing in the collection is a single preset, surface it
    // as a marker so the *parent* can collapse to bare-string form.
    if (compiledChildren.length === 0 && presetChild) return presetChild

    // If there are siblings, the preset has to be expanded so the
    // collection composes correctly.
    if (presetChild) {
      const expanded = expandPreset(presetChild.__preset)
      if (expanded) compiledChildren.push(expanded)
    }

    if (compiledChildren.length === 0) return null
    if (compiledChildren.length === 1) return compiledChildren[0]

    return { $type: 'filter-collection', method, filters: compiledChildren }
  }

  // Leaf predicate: drop if it has no usable value, otherwise project.
  if (!predicateHasValue(node)) return null
  return projectPredicate(node)
}

/**
 * Top-level entry point. Pass in an in-memory filter tree (as stored in
 * `config.scopeFilters[grouping]`) and receive the value to assign to
 * the grouping's `filter` field on the Orion request -- or null when
 * the field should be omitted entirely.
 *
 * Empirically, Orion's ContextFilterConverter does NOT handle a bare
 * predicate at `grouping.filter` -- the server accepts the polymorphic
 * shape but only the `filter-collection` and bare-string-preset variants
 * actually take effect. Single bare predicates are silently treated as
 * no-filter (every group passes), which manifests as "the filter doesn't
 * work" with no error. Always wrap a single surviving predicate in a
 * canonical filter-collection envelope at this level.
 *
 * Interior collections still collapse single children as a serialization
 * cleanliness pass; only the very top of the tree must be a collection
 * (or the bare-string preset shorthand).
 */
export function compileScopeFilter (tree) {
  const compiled = compileNode(tree)
  if (compiled == null) return null
  if (compiled.__preset) {
    // Whole tree reduced to a single preset -- emit as the bare-string
    // shorthand. Orion's ContextFilterConverter expands these server-side.
    return compiled.__preset
  }
  if (compiled.$type !== 'filter-collection') {
    return { $type: 'filter-collection', method: 'All', filters: [compiled] }
  }
  return compiled
}

/**
 * Counts the number of leaf predicates and presets in a tree. Used by the
 * UI to render filter-count badges on selected groupings.
 */
export function countFilterEntries (tree) {
  if (tree == null) return 0
  if (isPresetSentinel(tree)) return 1
  if (isCollection(tree)) {
    let n = 0
    for (const child of tree.filters || []) n += countFilterEntries(child)
    return n
  }
  return 1
}
